// Time tracking data hooks: running timer, tracker entries (paged by week), entry mutations and
// the weekly timesheet. Every mutation refreshes entries, timesheets, reports and header progress.
import {
  type ApplyTemplatesInput,
  type BulkEntriesInput,
  type BulkSet,
  type CopyLastWeekInput,
  localDate,
  type Progress,
  type TimeEntry,
  type TimeEntryInput,
  type TimeEntryPatch,
  type TimerStartInput,
  type TimerStopInput,
  type Timesheet,
  type TimesheetChangeRowInput,
  type TimesheetRemoveRowInput,
  type TimesheetRow,
  type TimesheetTemplate,
  type TimesheetTemplateInput,
  type TimesheetUpsertInput,
  type WeekPage,
  weekRange,
  zonedTimeToUtc,
} from '@taim/shared';
import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

export const timeKeys = {
  entries: ['time-entries'] as const,
  running: ['time-entries', 'running'] as const,
  tracker: (weekStart: string) => ['time-entries', 'tracker', weekStart] as const,
  range: (from: string, to: string, userId?: string) =>
    ['time-entries', 'range', from, to, userId ?? 'me'] as const,
  timesheets: ['timesheet'] as const,
  /** The signed-in user's week and month progress (header). */
  progress: ['progress'] as const,
  /** `userId`: a teammate's timesheet (admins and managers); omitted = the signed-in user's. */
  timesheet: (weekStart: string, userId?: string) => ['timesheet', weekStart, userId ?? 'me'] as const,
};
/** Owned by the reports feature; invalidated whenever time changes. */
export const reportsKey = ['reports'] as const;

/** The signed-in user's timezone and the workspace week start. */
export function useCalendar() {
  const { user, tenant } = useAuth();
  return { timeZone: user.timezone, weekStart: tenant.weekStart };
}

/** Today's date in the user's timezone, refreshed every minute (so "Today" rolls over). */
export function useToday(timeZone: string) {
  const [today, setToday] = useState(() => localDate(Date.now(), timeZone));
  useEffect(() => {
    const id = setInterval(() => setToday(localDate(Date.now(), timeZone)), 60_000);
    return () => clearInterval(id);
  }, [timeZone]);
  return today;
}

/** Current time, ticking every second while `active`. */
export function useNow(active: boolean) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

/** Refreshes everything derived from time entries: tracker, timesheets, reports and header progress. */
export function refreshTime(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: timeKeys.entries });
  qc.invalidateQueries({ queryKey: timeKeys.progress });
  qc.invalidateQueries({ queryKey: timeKeys.timesheets });
  qc.invalidateQueries({ queryKey: reportsKey });
}

// --- Instant tracker updates -------------------------------------------------------------------
// Saves show in the tracker list at once (edits before the server answers, new entries as soon as
// they are saved), and the server's answer then replaces them. The list's loaded weeks are only
// marked stale afterwards, not refetched one request per week: they reload when the list mounts
// again, or at once when the list can't show the answer itself (an entry moved to a week that
// isn't loaded, a timer stopped by starting another) or when the save failed (after rolling the
// change back). Running timer, header progress, timesheets and reports refresh as before.

type TrackerData = InfiniteData<WeekPage, string | null>;
const trackerPrefix = ['time-entries', 'tracker'] as const;
const byStartDesc = (a: TimeEntry, b: TimeEntry) => Date.parse(b.startAt) - Date.parse(a.startAt);

/** Rewrites the entries of every cached tracker page. */
function editTracker(qc: QueryClient, fn: (entries: TimeEntry[], page: WeekPage) => TimeEntry[]) {
  qc.setQueriesData<TrackerData>({ queryKey: trackerPrefix }, (data) =>
    data ? { ...data, pages: data.pages.map((p) => ({ ...p, entries: fn(p.entries, p) })) } : data,
  );
}

const trackerPages = (qc: QueryClient) =>
  qc.getQueriesData<TrackerData>({ queryKey: trackerPrefix }).flatMap(([, data]) => data?.pages ?? []);

const trackerEntry = (qc: QueryClient, id: string) =>
  trackerPages(qc)
    .flatMap((p) => p.entries)
    .find((e) => e.id === id);

/** Stops refetches already on their way, so they can't put the old entries back. */
const holdTracker = (qc: QueryClient) => qc.cancelQueries({ queryKey: trackerPrefix });

/**
 * After a tracker save: `reload` refetches the tracker's loaded weeks (those on screen); otherwise
 * they are only marked stale. Everything else derived from time refreshes; queries not on screen
 * (e.g. timesheets and reports while on the tracker) are only marked stale.
 */
function refreshAfterSave(qc: QueryClient, reload: boolean) {
  qc.invalidateQueries({ queryKey: trackerPrefix, refetchType: reload ? 'active' : 'none' });
  qc.invalidateQueries({ queryKey: timeKeys.entries, predicate: (q) => q.queryKey[1] !== 'tracker' });
  qc.invalidateQueries({ queryKey: timeKeys.progress });
  qc.invalidateQueries({ queryKey: timeKeys.timesheets });
  qc.invalidateQueries({ queryKey: reportsKey });
}

/** An entry as it will be after `patch` (the server's rules: a new project clears the task). */
function patched(e: TimeEntry, patch: TimeEntryPatch | BulkSet): TimeEntry {
  const next = { ...e };
  if (patch.description !== undefined) next.description = patch.description;
  if (patch.projectId !== undefined) {
    if (patch.projectId !== e.projectId) next.taskId = null;
    next.projectId = patch.projectId;
  }
  if (patch.taskId !== undefined) next.taskId = patch.taskId;
  if (patch.tagIds !== undefined) {
    next.tagIds =
      'tagMode' in patch && patch.tagMode === 'add'
        ? [...new Set([...e.tagIds, ...patch.tagIds])]
        : [...patch.tagIds];
  }
  if (patch.billable !== undefined) next.billable = patch.billable;
  if ('startAt' in patch && patch.startAt) next.startAt = patch.startAt;
  if ('endAt' in patch && patch.endAt) next.endAt = patch.endAt;
  if (next.endAt) next.durationSec = Math.round((Date.parse(next.endAt) - Date.parse(next.startAt)) / 1000);
  return next;
}

/** Writes to the cached tracker list (the signed-in user's entries, one page per week). */
function useTrackerCache() {
  const qc = useQueryClient();
  const { user, tenant } = useAuth();
  /** The start of the (user's) week an instant falls in: the tracker's page key. */
  const weekOf = (instant: string) => weekRange(new Date(instant), tenant.weekStart, user.timezone).start;
  /**
   * Shows `entries` in the list: replaced where they are, or added to the page of their week
   * (another user's entries leave it). False when one of them belongs to a week that isn't loaded.
   */
  const show = (entries: TimeEntry[]): boolean => {
    const ids = new Set(entries.map((e) => e.id));
    const mine = entries.filter((e) => e.userId === user.id);
    editTracker(qc, (current, page) => {
      const here = mine.filter((e) => page.weekStart === weekOf(e.startAt));
      if (here.length === 0 && !current.some((e) => ids.has(e.id))) return current;
      return [...current.filter((e) => !ids.has(e.id)), ...here].sort(byStartDesc);
    });
    const loaded = new Set(trackerPages(qc).map((p) => p.weekStart));
    return mine.every((e) => loaded.has(weekOf(e.startAt)));
  };
  return {
    qc,
    show,
    entry: (id: string) => trackerEntry(qc, id),
    hide: (ids: readonly string[]) =>
      editTracker(qc, (entries) =>
        entries.some((e) => ids.includes(e.id)) ? entries.filter((e) => !ids.includes(e.id)) : entries,
      ),
    /** After a save the list already shows: reloads it only if it couldn't show it all. */
    saved: (shown: boolean) => refreshAfterSave(qc, !shown),
    /** After a failed save: puts the entries back as they were, and reloads the list. */
    rollBack: (previous: TimeEntry[] | undefined) => {
      if (previous?.length) show(previous);
      refreshAfterSave(qc, true);
    },
  };
}

export function useRunningEntry() {
  const qc = useQueryClient();
  return useQuery({
    queryKey: timeKeys.running,
    queryFn: async () => {
      const running = await api.get<TimeEntry | null>('/api/time-entries/running');
      // A timer stopped or replaced elsewhere (another tab or device) is a finished entry now: the
      // tracker list (which isn't refetched on focus) reloads to show it.
      const before = qc.getQueryData<TimeEntry | null>(timeKeys.running);
      if (before && before.id !== running?.id) qc.invalidateQueries({ queryKey: trackerPrefix });
      return running;
    },
  });
}

/**
 * Tracker list: the current week first, then one earlier week (that has entries) per "Load more".
 * Refetching it means refetching every loaded week, so it isn't refetched when the window regains
 * focus (the running timer is, and reloads the list when it changed elsewhere), nor after saves
 * the list already shows.
 */
export function useTrackerEntries() {
  const { timeZone, weekStart } = useCalendar();
  const today = useToday(timeZone);
  const week = weekRange(today, weekStart, timeZone);
  return useInfiniteQuery({
    queryKey: timeKeys.tracker(week.start),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }): Promise<WeekPage> => {
      if (pageParam === null) {
        return {
          weekStart: week.start,
          entries: await api.get<TimeEntry[]>('/api/time-entries', { from: week.from }),
        };
      }
      return api.get<WeekPage>('/api/time-entries/week-before', { before: pageParam });
    },
    getNextPageParam: (last) =>
      last.weekStart ? zonedTimeToUtc(last.weekStart, 0, timeZone).toISOString() : undefined,
    refetchOnWindowFocus: false,
  });
}

export function useStartTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TimerStartInput) => api.post<TimeEntry>('/api/time-entries/start', input),
    // Starting stops the running timer (on the server): the list reloads to show it finished.
    onMutate: () => ({ stopsAnother: qc.getQueryData<TimeEntry | null>(timeKeys.running) != null }),
    onSuccess: (entry, _input, ctx) => {
      qc.setQueryData(timeKeys.running, entry);
      refreshAfterSave(qc, ctx.stopsAnother);
    },
    onError: () => refreshAfterSave(qc, true),
  });
}

/*
 * Stops per query client. An edit of the running entry sent before a stop may be answered after
 * it (still running); such a response must not put the stopped timer back into the cache.
 */
const stopCounts = new WeakMap<QueryClient, number>();
const stopCount = (qc: QueryClient) => stopCounts.get(qc) ?? 0;

export function useStopTimer() {
  const tracker = useTrackerCache();
  const { qc } = tracker;
  return useMutation({
    mutationFn: (input: TimerStopInput = {}) => api.post<TimeEntry>('/api/time-entries/stop', input),
    onMutate: () => {
      stopCounts.set(qc, stopCount(qc) + 1);
    },
    onSuccess: (stopped) => {
      qc.setQueryData(timeKeys.running, null);
      tracker.saved(tracker.show([stopped]));
    },
    onError: () => tracker.rollBack(undefined),
  });
}

export function useCreateEntry() {
  const tracker = useTrackerCache();
  return useMutation({
    mutationFn: (input: TimeEntryInput) => api.post<TimeEntry>('/api/time-entries', input),
    onSuccess: (entry) => tracker.saved(tracker.show([entry])),
    onError: () => tracker.rollBack(undefined),
  });
}

export function useUpdateEntry() {
  const tracker = useTrackerCache();
  const { qc } = tracker;
  return useMutation({
    mutationFn: ({ id, ...patch }: TimeEntryPatch & { id: string }) =>
      api.patch<TimeEntry>(`/api/time-entries/${id}`, patch),
    onMutate: async ({ id, ...patch }) => {
      const stops = stopCount(qc);
      const previous = tracker.entry(id);
      // Moving an entry to someone else takes it out of this list: leave that to the answer.
      if (previous && patch.userId === undefined) {
        await holdTracker(qc);
        tracker.show([patched(previous, patch)]);
      }
      return { stops, previous };
    },
    onSuccess: (entry, _patch, ctx) => {
      if (entry.endAt !== null) {
        tracker.saved(tracker.show([entry]));
        return;
      }
      // Only refresh the running timer if it is still this entry and no stop was sent since.
      const current = qc.getQueryData<TimeEntry | null>(timeKeys.running);
      if (current?.id === entry.id && ctx.stops === stopCount(qc)) {
        qc.setQueryData(timeKeys.running, entry);
      }
      tracker.saved(true);
    },
    onError: (_e, _patch, ctx) => tracker.rollBack(ctx?.previous && [ctx.previous]),
  });
}

export function useDeleteEntry() {
  const tracker = useTrackerCache();
  const { qc } = tracker;
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/time-entries/${id}`),
    onMutate: async (id) => {
      const previous = tracker.entry(id);
      await holdTracker(qc);
      tracker.hide([id]);
      return { previous };
    },
    onSuccess: (_, id) => {
      const running = qc.getQueryData<TimeEntry | null>(timeKeys.running);
      if (running?.id === id) qc.setQueryData(timeKeys.running, null);
      tracker.saved(true);
    },
    onError: (_e, _id, ctx) => tracker.rollBack(ctx?.previous && [ctx.previous]),
  });
}

export function useDuplicateEntry() {
  const tracker = useTrackerCache();
  return useMutation({
    mutationFn: (id: string) => api.post<TimeEntry>(`/api/time-entries/${id}/duplicate`),
    onSuccess: (entry) => tracker.saved(tracker.show([entry])),
    onError: () => tracker.rollBack(undefined),
  });
}

export function useBulkEntries() {
  const tracker = useTrackerCache();
  return useMutation({
    mutationFn: (input: BulkEntriesInput) =>
      api.post<TimeEntry[] | undefined>('/api/time-entries/bulk', input),
    onMutate: async (input) => {
      const previous = input.ids.map((id) => tracker.entry(id)).filter((e) => e !== undefined);
      await holdTracker(tracker.qc);
      if (input.action === 'delete') {
        tracker.hide(input.ids);
        return { previous };
      }
      // Details show at once; new times and dates or another user wait for the server.
      const { set } = input;
      if (set.userId === undefined && set.date === undefined && set.startMinute === undefined) {
        tracker.show(previous.map((e) => patched(e, set)));
      }
      return { previous };
    },
    onSuccess: (entries) => tracker.saved(entries ? tracker.show(entries) : true),
    onError: (_e, _input, ctx) => tracker.rollBack(ctx?.previous),
  });
}

/** The signed-in user's progress towards full hours this week and month. */
export function useProgress(today: string) {
  return useQuery({
    queryKey: [...timeKeys.progress, today],
    queryFn: () => api.get<Progress>('/api/progress', { date: today }),
    staleTime: 60_000,
  });
}

/** The week's timesheet of `userId` (admins and managers), or of the signed-in user. */
export function useTimesheet(weekStart: string, userId?: string) {
  return useQuery({
    queryKey: timeKeys.timesheet(weekStart, userId),
    queryFn: () => api.get<Timesheet>('/api/timesheet', { weekStart, userId }),
  });
}

type Cell = { projectId: string | null; taskId: string | null; day: number; value: number };
/** A cell a save changes, with its value before the save (to roll back just that cell). */
type CellChange = Cell & { previous: number };

const sameRow = (r: TimesheetRow, projectId: string | null, taskId: string | null) =>
  r.projectId === projectId && r.taskId === taskId;

/** Sets cells of a cached timesheet, adding rows as needed. */
function withCells(sheet: Timesheet, cells: Cell[]): Timesheet {
  const rows = sheet.rows.map((r) => ({ ...r, days: [...r.days] }));
  for (const c of cells) {
    let row = rows.find((r) => sameRow(r, c.projectId, c.taskId));
    if (!row) {
      row = { projectId: c.projectId, taskId: c.taskId, days: Array(7).fill(0) };
      rows.push(row);
    }
    row.days[c.day] = c.value;
  }
  return { ...sheet, rows };
}

/** Writes to one user's week run one at a time (saves and row removals). */
const sheetScope = (weekStart: string, userId?: string) => `timesheet-${weekStart}-${userId ?? 'me'}`;

/**
 * Saves timesheet cells with an optimistic update. Saves of a week run one at a time (a shared
 * mutation scope), so responses can't arrive out of order; the server's sheet is only written to
 * the cache by the last pending save, and a failed save rolls back just its own cells.
 */
export function useSaveTimesheet(weekStart: string, userId?: string) {
  const qc = useQueryClient();
  const key = timeKeys.timesheet(weekStart, userId);
  const mutationKey = ['timesheet-save', weekStart, userId ?? 'me'];
  const othersPending = () => qc.isMutating({ mutationKey }) > 1;
  return useMutation({
    mutationKey,
    scope: { id: sheetScope(weekStart, userId) },
    mutationFn: (input: Omit<TimesheetUpsertInput, 'userId'>) =>
      api.put<Timesheet>('/api/timesheet', { ...input, userId }),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const current = qc.getQueryData<Timesheet>(key);
      const changes: CellChange[] = [];
      for (const row of input.rows) {
        const taskId = row.taskId ?? null;
        const cached = current?.rows.find((r) => sameRow(r, row.projectId, taskId));
        row.days.forEach((value, day) => {
          if (value === null || value === undefined) return;
          changes.push({ projectId: row.projectId, taskId, day, value, previous: cached?.days[day] ?? 0 });
        });
      }
      if (current) qc.setQueryData<Timesheet>(key, withCells(current, changes));
      return { changes };
    },
    onError: (_e, _input, ctx) => {
      const current = qc.getQueryData<Timesheet>(key);
      if (current && ctx) {
        qc.setQueryData<Timesheet>(
          key,
          withCells(
            current,
            ctx.changes.map((c) => ({ ...c, value: c.previous })),
          ),
        );
      }
    },
    onSuccess: (sheet) => {
      // A later save is still queued: its optimistic cells stay until its own response arrives.
      if (!othersPending()) qc.setQueryData(key, sheet);
    },
    onSettled: () => {
      if (!othersPending()) qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: timeKeys.entries });
      qc.invalidateQueries({ queryKey: timeKeys.progress });
      qc.invalidateQueries({ queryKey: reportsKey });
    },
  });
}

/**
 * Clockify's row ✕: deletes the row's finished entries of the week. Queued behind the week's
 * cell saves (same scope), so a save sent just before can't bring the time back.
 */
export function useRemoveTimesheetRow(weekStart: string, userId?: string) {
  const qc = useQueryClient();
  const key = timeKeys.timesheet(weekStart, userId);
  return useMutation({
    scope: { id: sheetScope(weekStart, userId) },
    mutationFn: (input: Omit<TimesheetRemoveRowInput, 'weekStart' | 'userId'>) =>
      api.post<Timesheet>('/api/timesheet/remove-row', { userId, weekStart, ...input }),
    onSuccess: (sheet) => qc.setQueryData(key, sheet),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: timeKeys.entries });
      qc.invalidateQueries({ queryKey: timeKeys.progress });
      qc.invalidateQueries({ queryKey: reportsKey });
    },
  });
}

/**
 * Clockify's row project picker: moves the row's finished entries of the week to another
 * project/task. Queued behind the week's cell saves, like removing a row.
 */
export function useChangeTimesheetRow(weekStart: string, userId?: string) {
  const qc = useQueryClient();
  const key = timeKeys.timesheet(weekStart, userId);
  return useMutation({
    scope: { id: sheetScope(weekStart, userId) },
    mutationFn: (input: Omit<TimesheetChangeRowInput, 'weekStart' | 'userId'>) =>
      api.post<Timesheet>('/api/timesheet/change-row', { userId, weekStart, ...input }),
    onSuccess: (sheet) => qc.setQueryData(key, sheet),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: timeKeys.entries });
      qc.invalidateQueries({ queryKey: timeKeys.progress });
      qc.invalidateQueries({ queryKey: reportsKey });
    },
  });
}

/**
 * Entries starting in [from, to) (ISO instants), the running timer too: the signed-in user's, or
 * `userId`'s (admins and managers).
 */
export function useEntriesBetween(
  from: string,
  to: string,
  opts: { enabled?: boolean; userId?: string } = {},
) {
  return useQuery({
    queryKey: timeKeys.range(from, to, opts.userId),
    queryFn: () => api.get<TimeEntry[]>('/api/time-entries', { from, to, userId: opts.userId }),
    enabled: opts.enabled ?? true,
  });
}

/**
 * After copying last week or applying templates: the week's rows with time become the cached
 * timesheet (the empty ones are added locally by the caller), and everything derived refreshes.
 */
function showAddedTime(qc: QueryClient, sheet: Timesheet, userId?: string) {
  if (sheet.rows.some((r) => r.days.some((d) => d > 0))) {
    qc.setQueryData(timeKeys.timesheet(sheet.weekStart, userId), {
      ...sheet,
      rows: sheet.rows.filter((r) => r.days.some((d) => d > 0)),
    });
  }
  qc.invalidateQueries({ queryKey: timeKeys.entries });
  qc.invalidateQueries({ queryKey: timeKeys.progress });
  qc.invalidateQueries({ queryKey: reportsKey });
}

export function useCopyLastWeek(userId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CopyLastWeekInput, 'userId'>) =>
      api.post<Timesheet>('/api/timesheet/copy-last-week', { ...input, userId }),
    onSuccess: (sheet) => showAddedTime(qc, sheet, userId),
  });
}

/** The signed-in user's timesheet templates (private to them). */
export const templateKeys = ['timesheet-templates'] as const;

export function useTimesheetTemplates(enabled = true) {
  return useQuery({
    queryKey: templateKeys,
    queryFn: () => api.get<TimesheetTemplate[]>('/api/timesheet-templates'),
    enabled,
  });
}

export function useSaveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TimesheetTemplateInput) =>
      api.post<TimesheetTemplate>('/api/timesheet-templates', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: templateKeys }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/timesheet-templates/${id}`),
    onSuccess: (_, id) =>
      qc.setQueryData<TimesheetTemplate[]>(templateKeys, (list) => list?.filter((t) => t.id !== id)),
    onSettled: () => qc.invalidateQueries({ queryKey: templateKeys }),
  });
}

export function useApplyTemplates(userId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<ApplyTemplatesInput, 'userId'>) =>
      api.post<Timesheet & { skippedCells?: number }>('/api/timesheet/apply-templates', { ...input, userId }),
    onSuccess: (sheet) => showAddedTime(qc, sheet, userId),
  });
}
